import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import { formatPrice, formatNumber, formatTimeAgo } from '../utils/formatters';

const HomePage = () => {
  const navigate = useNavigate();
  const { domains, transactions } = useApp();

  // Get trending domains
  const trendingDomains = domains
    .filter(d => d.trending || d.isNew)
    .slice(0, 6);

  // Get recent big sales
  const recentSales = transactions
    .filter(t => t.type === 'sale')
    .sort((a, b) => b.price - a.price)
    .slice(0, 5);

  // How it works steps
  const steps = [
    {
      icon: '🔍',
      title: 'Find Your Domain',
      description: 'Search or browse AI-recommended domains from thousands of premium listings'
    },
    {
      icon: '⚡',
      title: 'Trade Instantly',
      description: 'Buy now, bid, or swap with other domains. Complete transactions in seconds'
    },
    {
      icon: '📈',
      title: 'Profit & Repeat',
      description: 'Track portfolio, flip for profit, and climb the leaderboards'
    }
  ];

  // Features preview
  const features = [
    { icon: '⚡', title: 'Instant Trading', description: 'Buy and sell in seconds' },
    { icon: '🤖', title: 'AI Valuations', description: 'Smart pricing recommendations' },
    { icon: '🔄', title: 'Domain Swaps', description: 'Trade domains for other domains' },
    { icon: '📊', title: 'Live Leaderboards', description: 'Compete with top traders' },
    { icon: '💬', title: 'Social Trading', description: 'Follow, comment, discover' },
    { icon: '🏆', title: 'Achievements', description: 'Earn badges and rewards' }
  ];

  // Stats
  const stats = [
    { value: '$5.2M+', label: 'Total Traded' },
    { value: '10,000+', label: 'Domains Listed' },
    { value: '8,500+', label: 'Active Traders' },
    { value: '4.2M+', label: 'Total Transactions' }
  ];

  // Testimonials
  const testimonials = [
    {
      avatar: 'https://i.pravatar.cc/150?img=11',
      name: 'Sarah Chen',
      username: '@DomainKing',
      quote: 'Flipped 47 domains in 30 days. The AI valuations are incredibly accurate!',
      stats: '$127K profit in first month'
    },
    {
      avatar: 'https://i.pravatar.cc/150?img=12',
      name: 'Mike Johnson',
      username: '@StartupQueen',
      quote: 'Found the perfect domain for my SaaS in 5 minutes. So much easier than traditional brokers.',
      stats: 'Saved $5K in fees'
    },
    {
      avatar: 'https://i.pravatar.cc/150?img=13',
      name: 'Alex Kim',
      username: '@CryptoFlip',
      quote: 'Made 400% ROI on AINews.io in just 2 weeks. This platform is a game changer.',
      stats: '15 successful flips'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 text-center">
          <p className="text-2xl md:text-3xl font-semibold mb-4 text-gray-700">
            Next-Generation Domain Trading
          </p>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
            Discover the Next Unicorn<br />Before It Exists
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            The fastest way to buy, sell, and swap premium domain names.
            Find trending domains that could become the next Apple, Amazon, or Tesla.
            <br /><br />Instant. Social. Profitable.
          </p>
          <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto">
            AI-powered valuations. Real-time trading. Join 10,000+ domain traders.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/marketplace')}>
              Start Trading
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/demo')}>
              Watch Demo
            </Button>
          </div>
          <div className="mt-8">
            <a
              href="https://nerktendo.com"
              target="_blank"
              rel="noreferrer"
              className="text-gray-500 hover:text-gray-700 transition-colors text-sm"
            >
              A NERKTEK CREATION
            </a>
          </div>
        </div>
      </section>

      {/* Live Activity Ticker */}
      <div className="bg-white border-y border-gray-200 py-4 overflow-hidden">
        <div className="flex animate-scroll whitespace-nowrap">
          {transactions.slice(0, 10).map((transaction, index) => (
            <div key={index} className="inline-flex items-center mx-8">
              <span className="text-sm text-gray-600">
                {transaction.type === 'sale' ? '💰' : '🎯'} {transaction.domain} {transaction.type === 'sale' ? 'sold' : 'bid'} for {formatPrice(transaction.price)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Trending Domains Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            🔥 Hot Domains Right Now
          </h2>
          <p className="text-gray-600 text-center mb-12">
            Most popular domains in the last 24 hours
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trendingDomains.map(domain => (
              <Card
                key={domain.id}
                hover
                className="p-6 cursor-pointer"
                onClick={() => navigate(`/domain/${domain.name}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold">{domain.name}</h3>
                  {domain.trending && <Badge variant="warning" size="sm">🔥 Hot</Badge>}
                </div>
                <p className="text-3xl font-bold text-blue-600 mb-4">
                  {formatPrice(domain.price)}
                </p>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>👁 {formatNumber(domain.views)} views</span>
                  <span>❤️ {domain.likes} likes</span>
                </div>
                <Button fullWidth className="mt-4" size="sm">
                  View Details
                </Button>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button variant="outline" onClick={() => navigate('/marketplace')}>
              View All Domains
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">{step.icon}</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">
            Join Top Domain Traders
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 border-white/20 backdrop-blur p-6">
                <div className="flex items-center gap-4 mb-4">
                  <Avatar src={testimonial.avatar} size="lg" />
                  <div>
                    <p className="font-bold">{testimonial.name}</p>
                    <p className="text-sm text-blue-200">{testimonial.username}</p>
                  </div>
                </div>
                <p className="text-white/90 mb-4">{testimonial.quote}</p>
                <p className="text-blue-200 text-sm">{testimonial.stats}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Big Sales */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Recent Big Sales</h2>
          <div className="space-y-4">
            {recentSales.map(sale => (
              <Card key={sale.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-bold text-lg">{sale.domain}</p>
                    <p className="text-sm text-gray-600">
                      @{sale.seller} → @{sale.buyer} • {formatTimeAgo(sale.timestamp)}
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {formatPrice(sale.price)}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Preview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">
            Powerful Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} hover className="p-6 text-center">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button onClick={() => navigate('/features')}>
              Explore All Features
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Trading?</h2>
          <p className="text-xl mb-8">Join thousands of domain traders today</p>
          <Button size="lg" variant="secondary" onClick={() => navigate('/marketplace')}>
            Explore Marketplace
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
